import { ButtonIcon, Callout, css, styled, SyncIcon, Tooltip } from 'canoe';
import pickBy from 'lodash/pickBy';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ColorResult, SketchPicker } from 'react-color';
import tinycolor from 'tinycolor2';

import { useDefaultVariants } from '@candu-ai/styleguide/application-variants';

import { ValueBox } from '../ValueBox';
import { AtLeastOne } from '../ValueBox/ValueBox';
import { ValueToggle } from '../ValueToggle';

interface PreviewProps {
  disabled: boolean;
  background?: string;
}

const Preview = styled.div<PreviewProps>`
  ${({ theme, disabled, background }) => css`
    width: 32px;
    height: 32px;
    cursor: pointer;
    background: ${theme.colors.White};
    position: relative;

    ${background &&
    css`
      background: ${background};
    `}

    ${disabled &&
    css`
      :after {
        content: ' ';
        display: block;
        width: 80px;
        height: 1px;
        position: absolute;
        background: ${theme.colors.Danger};
        transform: rotate(-45deg);
        transform-origin: right;
        top: 0;
        right: 0;
      }
    `}
  `}
`;

const ColorValue = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 135px;
    padding: ${theme.spaces.xs} ${theme.spaces.xxs} ${theme.spaces.xs} ${theme.spaces.xs};
  `}
`;

const ColorInput = styled.input`
  ${({ theme }) => css`
    width: 100px;
    height: 100%;
    background: none;
    padding: 0;
    color: ${theme.colors.OnyxGrayD10};
    text-transform: uppercase;
    border: 1px solid transparent;
    z-index: 1;
    outline: none;

    &:focus,
    &:active {
      border-color: ${theme.colors.borderFocus};
    }

    &[value='transparent'],
    &[value='default'] {
      text-transform: capitalize;
    }
  `}
`;

interface Props {
  value?: unknown;
  label?: string;
  onResetTooltip?: string;
  onChange?: (color?: string) => void;
  defaultValue?: 'transparent' | 'default' | string;
  isInGroup?:
    | boolean
    | AtLeastOne<{
        isFirst: boolean;
        isLast: boolean;
      }>;
}

const hexRegexp = new RegExp(/^([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i);
const hashHexRegexp = new RegExp(/^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i);

export const TRANSPARENT = 'transparent';
export const UNSET = 'default';

const isValidColorInput = (inputColor: string) =>
  hashHexRegexp.test(inputColor) || [TRANSPARENT, UNSET].includes(inputColor);

export const ColorPicker = ({
  value: unknownValue,
  onChange,
  defaultValue = TRANSPARENT,
  label = 'Color',
  isInGroup,
  onResetTooltip,
}: Props) => {
  const value = unknownValue as string;
  const { data } = useDefaultVariants();
  const { variants: styleguideVariants } = data || {};

  const colorInputRef = useRef<HTMLInputElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);

  const [inputColor, setInputColor] = useState<string | undefined>(value);
  const [hasFocus, changeFocus] = useState(false);

  const [pickerVisible, setPickerVisible] = useState(false);
  const [pickerColor, setPickerColor] = useState(value);

  const variables = styleguideVariants?.Global.base.selectors.global.rules;
  const presetColors = Object.values(pickBy(variables, (_, key) => key.startsWith('--color-')));

  const selectColor = (colorValue?: string) => {
    setInputColor(colorValue);
    // picker uses internal state to allow for different input methods
    // but we still forward onChange value to parent and sync value with state
    if (onChange) {
      onChange(colorValue !== defaultValue ? colorValue : undefined);
    }
  };

  useEffect(() => {
    if (value !== inputColor && !hasFocus) {
      // only trigger on external value changes while not editing the input
      setInputColor(value);
      setPickerColor(value);
    }
  }, [value, inputColor, hasFocus]);

  const colorToHex = (parsedColor: tinycolor.Instance) =>
    parsedColor.getAlpha() < 1 ? parsedColor.toHex8String() : parsedColor.toHexString();

  const adaptPickerValue = (pickerValue: ColorResult) => colorToHex(tinycolor(pickerValue.rgb));

  const setColorManually = (newColor?: string) => {
    setInputColor(newColor);

    if (newColor) {
      if (hexRegexp.test(newColor)) {
        setColorManually(`#${newColor}`);
      } else if (isValidColorInput(newColor)) {
        setPickerColor(newColor);
        selectColor(newColor);
      }
    }
  };

  const resetColorIfInvalid = () => {
    changeFocus(false);
    if (inputColor === '') {
      selectColor(defaultValue);
      return;
    }
    if (!inputColor || !isValidColorInput(inputColor)) {
      setInputColor(value);
    }
  };

  const onReset = () => {
    selectColor(undefined);
    setInputColor(defaultValue);
    setPickerColor(defaultValue);
  };

  let background = value;

  const displayColor = useMemo(() => {
    if (hasFocus && (displayColor === TRANSPARENT || displayColor === UNSET)) {
      return '';
    }

    if (hasFocus) {
      return inputColor;
    }

    return inputColor !== undefined ? inputColor : defaultValue;
  }, [inputColor, defaultValue, hasFocus]);

  if (displayColor === TRANSPARENT) {
    background =
      'url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQCAYAAAAf8/9hAAAAMUlEQVQ4T2NkYGAQYcAP3uCTZhw1gGGYhAGBZIA/nYDCgBDAm9BGDWAAJyRCgLaBCAAgXwixzAS0pgAAAABJRU5ErkJggg==")';
  }

  if (displayColor === UNSET) {
    background = 'none';
  }

  if (!background && defaultValue && defaultValue !== UNSET && defaultValue !== TRANSPARENT) {
    background = defaultValue;
  }

  const ResetTooltip = ({ children }: any) =>
    onResetTooltip ? (
      <Tooltip content={onResetTooltip} placement="top">
        {children}
      </Tooltip>
    ) : (
      <>{children}</>
    );

  return (
    <div css={{ position: 'relative' }}>
      <ValueBox data-cy="color-picker-box" label={label} isInGroup={isInGroup}>
        <ValueToggle
          toggleGroup="colorpicker-toggle"
          label={
            <ColorValue>
              <ColorInput
                data-cy="colorpicker-input"
                ref={colorInputRef}
                onChange={(e: any) => setColorManually(e.target.value)}
                value={displayColor || ''}
                onBlur={resetColorIfInvalid}
                onFocus={() => {
                  changeFocus(true);
                  colorInputRef.current?.select();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === 'Escape') {
                    colorInputRef.current?.blur();
                  }
                }}
              />
              {!!inputColor && inputColor !== defaultValue && (
                <ResetTooltip>
                  <ButtonIcon
                    data-cy="colorpicker-clear"
                    icon={SyncIcon}
                    color="contrast"
                    size="xsmall"
                    onClick={onReset}
                  />
                </ResetTooltip>
              )}
            </ColorValue>
          }
          responsiveWidth
        />
        <ValueToggle
          toggleGroup="colorpicker-toggle"
          label={
            <Preview
              ref={previewRef}
              disabled={displayColor === UNSET}
              background={background}
              onClick={() => window.requestAnimationFrame(() => setPickerVisible(!pickerVisible))}
            />
          }
        />
      </ValueBox>

      {pickerVisible && (
        <Callout
          usePortal={false}
          referenceElement={previewRef.current}
          closeKey="Escape"
          placement="bottom-end"
          onClose={() => setPickerVisible(false)}
        >
          {(calloutProps) => (
            <div
              ref={calloutProps.ref}
              style={{ ...calloutProps.style, zIndex: 3 }}
              {...calloutProps.attributes.popper}
            >
              <SketchPicker
                color={pickerColor}
                onChange={(pickerValue) => setPickerColor(adaptPickerValue(pickerValue))}
                onChangeComplete={(pickerValue) => selectColor(adaptPickerValue(pickerValue))}
                presetColors={presetColors}
              />
            </div>
          )}
        </Callout>
      )}
    </div>
  );
};
